import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import CallMadeRounded from '@mui/icons-material/CallMadeRounded';
import CallReceivedRounded from '@mui/icons-material/CallReceivedRounded';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { EmptyState } from '../../../components/states';
import { StatusChip } from '../../../components/StatusChip';
import { businessDate, moneyCompact } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { BackLink, PrintStyles } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { NoAccess, QueryError, StatGrid, StatTile } from '../components/ui';
import { linkTo } from '../components/useEntityLink';
import { useReportParams, usePeriodText } from '../components/useReportParams';
import { ACCOUNT_TYPES } from '../lib/accounts';
import { num } from '../lib/numbers';
import { M, RangeControls } from '../reports/common';

/** General ledger of one account: opening, every posted line with running balance, closing. */
export function AccountLedgerPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const p = useReportParams('thisMonth');
  const period = usePeriodText(p);
  const query = { ...p.range, page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['account-ledger', id, query],
    queryFn: () => unwrap(api.GET('/accounts/{id}/ledger', { params: { path: { id }, query } })),
    placeholderData: keepPreviousData,
    enabled: can('journal.view'),
  });
  if (!can('journal.view'))
    return (
      <>
        <BackLink to="/accounting" label="Accounting" />
        <NoAccess what="account ledgers" />
      </>
    );
  const d = q.data;
  // The API gives debit − credit; show credit-normal accounts on their natural side.
  const sign = d?.account.normalBalance === 'CREDIT' ? -1 : 1;
  const nat = (v: string | undefined) => (v === undefined ? undefined : num(v) * sign || 0);
  const typeLabel = ACCOUNT_TYPES.find((t) => t.value === d?.account.type)?.label;
  return (
    <>
      <PrintStyles />
      <BackLink to="/accounting" label="Accounting" />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, mb: 0.5 }}
      >
        <Typography variant="h3">
          {d ? `${d.account.code} · ${d.account.name}` : 'Account ledger'}
        </Typography>
        {d && (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={typeLabel} variant="outlined" />
            <Chip
              size="small"
              label={`${d.account.normalBalance === 'DEBIT' ? 'Debit' : 'Credit'} balance`}
              variant="outlined"
            />
          </Stack>
        )}
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2.5 }}>
        General ledger · {period}
      </Typography>
      <Stack direction="row" className="no-print" sx={{ gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
        <RangeControls p={p} />
      </Stack>
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Opening balance"
          value={d && moneyCompact(nat(d.openingBalance), format)}
          icon={<AccountBalanceOutlined />}
          tone="neutral"
          loading={q.isLoading}
        />
        <StatTile
          label="Debits"
          value={d && moneyCompact(d.totals.debit, format)}
          hint={d && `${String(d.totals.entries)} entries`}
          icon={<CallReceivedRounded />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Credits"
          value={d && moneyCompact(d.totals.credit, format)}
          icon={<CallMadeRounded />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label="Closing balance"
          value={d && moneyCompact(nat(d.closingBalance), format)}
          icon={<AccountBalanceOutlined />}
          loading={q.isLoading}
        />
      </StatGrid>
      <ReportTable
        loading={q.isLoading}
        rows={d?.data}
        rowKey={(r) => r.lineId}
        page={d?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        onRowClick={(r) => void navigate(`/accounting/journal/${r.entryId}`)}
        dense
        showTotals={false}
        empty={
          <EmptyState
            icon={<MenuBookOutlined />}
            title="No entries in this period"
            body="Try a longer date range."
          />
        }
        toolbar={
          d &&
          num(d.pageOpeningBalance) !== 0 && (
            <Typography variant="body2" color="text.secondary">
              Balance brought forward: <M v={nat(d.pageOpeningBalance)} strong />
            </Typography>
          )
        }
        columns={[
          {
            key: 'date',
            header: 'Date',
            hideBelow: 'sm',
            width: 120,
            render: (r) => businessDate(r.entryDate, format),
          },
          {
            key: 'doc',
            header: 'Document',
            render: (r) => {
              const to = linkTo(r.document.type, r.document.id);
              const label = r.document.number ?? r.document.label;
              return (
                <Box sx={{ minWidth: 0 }}>
                  {to ? (
                    <Link
                      component={RouterLink}
                      to={to}
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontWeight: 600 }}
                    >
                      {label}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {label}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ display: { sm: 'none' } }}
                  >
                    {businessDate(r.entryDate, format)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {[
                      r.document.label !== label ? r.document.label : null,
                      r.contact?.name,
                      r.description && !r.description.includes(label) ? r.description : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              );
            },
          },
          {
            key: 'status',
            header: 'Status',
            hideBelow: 'md',
            render: (r) =>
              r.status === 'POSTED' ? null : (
                <StatusChip
                  status={r.status === 'REVERSED' ? 'VOID' : 'INFO'}
                  label={r.status === 'REVERSED' ? 'Reversed' : 'Reversal'}
                />
              ),
          },
          {
            key: 'debit',
            header: 'Debit',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => (num(r.debit) ? <M v={r.debit} /> : ''),
          },
          {
            key: 'credit',
            header: 'Credit',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => (num(r.credit) ? <M v={r.credit} /> : ''),
          },
          {
            key: 'balance',
            header: 'Balance',
            align: 'right',
            render: (r) => <M v={nat(r.balance)} strong signed />,
          },
        ]}
      />
    </>
  );
}
